/**
 * EditSchedulePage
 * ----------------
 * Admin-facing schedule editor enabling point-and-click updates to sessions and slots.
 * Provides the ability to toggle slot availability (blocked slots stay hidden from attendees)
 * and reuses the same payload as the schedule import workflow.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

import useApi from '../hooks/useApi';

const unwrapApiRows = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response.rows)) {
    return response.rows;
  }
  if (Array.isArray(response.data?.rows)) {
    return response.data.rows;
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response.items)) {
    return response.items;
  }
  return [];
};

const getTodayDateValue = () => new Date().toISOString().split('T')[0];

const createEmptySlot = (overrides = {}) => ({
  slotLabel: '',
  maxCapacity: 1,
  specialNote: '',
  defaultPrice: '',
  isBlocked: false,
  ...overrides,
});

const createEmptySession = (overrides = {}) => ({
  sessionNumber: null,
  sessionLabel: '',
  timeSlot: '',
  specialNote: '',
  slots: [createEmptySlot()],
  ...overrides,
});

const EditSchedulePage = () => {
  const navigate = useNavigate();
  const { rageRoom } = useApi();

  const locationId = process.env.REACT_APP_DEFAULT_LOCATION_ID || 'default-location';
  const defaultTimezone = process.env.REACT_APP_DEFAULT_TIMEZONE || 'America/Los_Angeles';

  const [rawRooms, setRawRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomsError, setRoomsError] = useState(null);

  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [scheduleDate, setScheduleDate] = useState(getTodayDateValue);
  const [timezone, setTimezone] = useState(defaultTimezone);

  const [sessions, setSessions] = useState([createEmptySession()]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [loadedScheduleMeta, setLoadedScheduleMeta] = useState(null);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchRooms = async () => {
      if (!rageRoom?.admin?.listRooms) {
        setRoomsError('Admin room service is not available.');
        setRoomsLoading(false);
        return;
      }

      setRoomsLoading(true);
      setRoomsError(null);

      try {
        const response = await rageRoom.admin.listRooms({ locationId });
        if (!isMounted) {
          return;
        }
        setRawRooms(Array.isArray(response?.rows) ? response.rows : unwrapApiRows(response));
      } catch (error) {
        if (isMounted) {
          setRoomsError(error.message || 'Unable to load rage rooms.');
          setRawRooms([]);
        }
      } finally {
        if (isMounted) {
          setRoomsLoading(false);
        }
      }
    };

    fetchRooms();

    return () => {
      isMounted = false;
    };
  }, [locationId]);

  const normalizedRooms = useMemo(
    () =>
      rawRooms
        .map((room) => ({
          id: room.id || room._id || room.roomId || room.slug || '',
          name: room.name || room.displayName || 'Unnamed Room',
          city: room.city || room.location?.city || room.locationName || 'Unknown City',
        }))
        .filter((room) => room.id),
    [rawRooms]
  );

  const selectedRoom = useMemo(
    () => normalizedRooms.find((room) => room.id === selectedRoomId) || null,
    [normalizedRooms, selectedRoomId]
  );

  const blockedSlotCount = useMemo(
    () =>
      sessions.reduce(
        (total, session) => total + session.slots.filter((slot) => slot.isBlocked).length,
        0
      ),
    [sessions]
  );

  const sessionsValid = useMemo(
    () =>
      sessions.length > 0 &&
      sessions.every((session) => {
        if (!session.sessionLabel.trim() || !session.timeSlot) {
          return false;
        }
        if (!session.slots.length) {
          return false;
        }
        return session.slots.every(
          (slot) => slot.slotLabel.trim() && Number(slot.maxCapacity) > 0
        );
      }),
    [sessions]
  );

  const canSubmit = Boolean(
    selectedRoomId && scheduleDate && sessionsValid && !submitLoading && !loadingSchedule
  );

  const handleAddSession = () => {
    setSessions((prev) => [...prev, createEmptySession({ sessionNumber: prev.length + 1 })]);
  };

  const handleRemoveSession = (index) => {
    if (sessions.length === 1) {
      setSessions([createEmptySession()]);
      return;
    }
    setSessions((prev) => prev.filter((_, sessionIndex) => sessionIndex !== index));
  };

  const handleSessionFieldChange = (index, field) => (event) => {
    const value = event.target.value;
    setSessions((prev) =>
      prev.map((session, sessionIndex) =>
        sessionIndex === index ? { ...session, [field]: value } : session
      )
    );
  };

  const handleAddSlot = (sessionIndex) => {
    setSessions((prev) =>
      prev.map((session, index) =>
        index === sessionIndex
          ? {
              ...session,
              slots: [...session.slots, createEmptySlot()],
            }
          : session
      )
    );
  };

  const handleRemoveSlot = (sessionIndex, slotIndex) => {
    setSessions((prev) =>
      prev.map((session, sIdx) => {
        if (sIdx !== sessionIndex) {
          return session;
        }
        if (session.slots.length === 1) {
          return session;
        }
        return {
          ...session,
          slots: session.slots.filter((_, sIndex) => sIndex !== slotIndex),
        };
      })
    );
  };

  const handleSlotFieldChange = (sessionIndex, slotIndex, field) => (event) => {
    const value = event.target.value;
    setSessions((prev) =>
      prev.map((session, sIdx) => {
        if (sIdx !== sessionIndex) {
          return session;
        }
        return {
          ...session,
          slots: session.slots.map((slot, sIndex) => {
            if (sIndex !== slotIndex) {
              return slot;
            }
            if (field === 'maxCapacity') {
              return { ...slot, maxCapacity: Math.max(Number(value) || 1, 1) };
            }
            return { ...slot, [field]: value };
          }),
        };
      })
    );
  };

  const handleSlotBlockedToggle = (sessionIndex, slotIndex) => (event) => {
    const checked = event.target.checked;
    setSessions((prev) =>
      prev.map((session, sIdx) => {
        if (sIdx !== sessionIndex) {
          return session;
        }
        return {
          ...session,
          slots: session.slots.map((slot, sIndex) =>
            sIndex === slotIndex ? { ...slot, isBlocked: checked } : slot
          ),
        };
      })
    );
  };

  const handleResetEditor = () => {
    setSessions([createEmptySession()]);
    setTimezone(defaultTimezone);
    setScheduleDate(getTodayDateValue());
    setScheduleInfo(null);
    setScheduleError(null);
    setSubmitError(null);
    setSubmitSuccess(null);
    setLoadedScheduleMeta(null);
  };

  const loadSchedule = useCallback(async () => {
    if (!selectedRoomId || !scheduleDate) {
      setScheduleError('Select both a rage room and schedule date before loading.');
      return;
    }
    if (!rageRoom?.admin?.listSchedule) {
      setScheduleError('Schedule service is not available.');
      return;
    }

    setLoadingSchedule(true);
    setScheduleError(null);
    setScheduleInfo(null);
    setSubmitError(null);

    try {
      const response = await rageRoom.admin.listSchedule({
        locationId,
        roomId: selectedRoomId,
        query: { date: scheduleDate },
      });
      const rows = unwrapApiRows(response);

      if (!rows.length) {
        setSessions([createEmptySession()]);
        setLoadedScheduleMeta(null);
        setTimezone(defaultTimezone);
        setScheduleInfo('No schedule exists for that date yet. Add sessions below to get started.');
        return;
      }

      const schedule = rows[0];
      const formattedSessions =
        (schedule.sessions || []).map((session) =>
          createEmptySession({
            sessionNumber: session.sessionNumber,
            sessionLabel: session.sessionLabel || '',
            timeSlot: session.timeSlot || '',
            specialNote: session.specialNote || '',
            slots: (session.slots || []).length
              ? session.slots.map((slot) =>
                  createEmptySlot({
                    slotLabel: slot.slotLabel || '',
                    maxCapacity: Math.max(Number(slot.maxCapacity) || 1, 1),
                    specialNote: slot.specialNote || '',
                    defaultPrice:
                      slot.defaultPrice === null || slot.defaultPrice === undefined
                        ? ''
                        : String(slot.defaultPrice),
                    isBlocked: slot.isBlocked === true,
                  })
                )
              : [createEmptySlot()],
          })
        ) || [];

      setSessions(formattedSessions.length ? formattedSessions : [createEmptySession()]);
      setTimezone(schedule.timezone || defaultTimezone);
      setLoadedScheduleMeta({
        roomName: schedule.roomName || schedule.room?.name || selectedRoom?.name || '',
        date: schedule.date,
        timezone: schedule.timezone || defaultTimezone,
        totalSessions: schedule.sessions?.length || formattedSessions.length,
        lastUpdated: schedule.updatedAt || schedule.createdAt || null,
      });
      setScheduleInfo(`Schedule loaded for ${schedule.date}.`);
    } catch (error) {
      setScheduleError(error.message || 'Unable to load schedule.');
      setSessions([createEmptySession()]);
      setLoadedScheduleMeta(null);
    } finally {
      setLoadingSchedule(false);
    }
  }, [defaultTimezone, locationId, scheduleDate, selectedRoom, selectedRoomId]);

  useEffect(() => {
    if (!selectedRoomId || !scheduleDate) {
      return;
    }
    loadSchedule();
  }, [loadSchedule, scheduleDate, selectedRoomId]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!rageRoom?.admin?.upsertSchedule) {
      setSubmitError('Schedule update service is not available.');
      return;
    }

  if (!canSubmit) {
      setSubmitError('Please complete all required fields before saving.');
      return;
    }

    setSubmitLoading(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const payload = {
        date: scheduleDate,
        timezone: timezone.trim() || defaultTimezone,
        replaceExisting: true,
        sessions: sessions.map((session, sessionIndex) => ({
          sessionNumber: session.sessionNumber || sessionIndex + 1,
          sessionLabel: session.sessionLabel.trim(),
          timeSlot: session.timeSlot,
          specialNote: session.specialNote.trim(),
          slots: session.slots.map((slot, slotIndex) => {
            const normalizedSlot = {
              slotLabel: slot.slotLabel.trim() || `Slot ${slotIndex + 1}`,
              maxCapacity: Math.max(Number(slot.maxCapacity) || 1, 1),
              specialNote: slot.specialNote.trim(),
              isBlocked: Boolean(slot.isBlocked),
            };
            if (slot.defaultPrice !== '') {
              const priceNumber = Number(slot.defaultPrice);
              if (Number.isFinite(priceNumber) && priceNumber >= 0) {
                normalizedSlot.defaultPrice = priceNumber;
              }
            }
            return normalizedSlot;
          }),
        })),
      };

      await rageRoom.admin.upsertSchedule({
        locationId,
        roomId: selectedRoomId,
        payload,
      });

      setSubmitSuccess('Schedule updated successfully.');
      setScheduleInfo('Latest schedule saved.');
      await loadSchedule();
    } catch (error) {
      setSubmitError(error.message || 'Unable to save schedule right now.');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={4}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              Schedule Editor
            </Typography>
            <Typography variant="h4" gutterBottom>
              Edit Rage Room Schedule
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 640 }}>
              Load an existing schedule, tweak session metadata, and block individual slots without
              touching JSON. Blocked slots remain hidden from public availability and cannot be booked.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button variant="outlined" color="inherit" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button type="submit" variant="contained" disabled={!canSubmit}>
              {submitLoading ? 'Saving…' : 'Save Changes'}
            </Button>
          </Stack>
        </Stack>

        {roomsError && (
          <Alert severity="error" onClose={() => setRoomsError(null)}>
            {roomsError}
          </Alert>
        )}
        {scheduleError && (
          <Alert severity="error" onClose={() => setScheduleError(null)}>
            {scheduleError}
          </Alert>
        )}
        {scheduleInfo && (
          <Alert severity="info" onClose={() => setScheduleInfo(null)}>
            {scheduleInfo}
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}
        {submitSuccess && (
          <Alert severity="success" onClose={() => setSubmitSuccess(null)}>
            {submitSuccess}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6">Room & Date</Typography>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Rage Room"
                    value={selectedRoomId}
                    onChange={(event) => setSelectedRoomId(event.target.value)}
                    required
                    fullWidth
                    helperText={
                      roomsLoading
                        ? 'Loading rooms…'
                        : 'Choose the room whose schedule you need to edit.'
                    }
                    disabled={roomsLoading || !normalizedRooms.length}
                  >
                    {roomsLoading && (
                      <MenuItem value="">
                        <CircularProgress size={18} sx={{ mr: 1 }} /> Loading…
                      </MenuItem>
                    )}
                    {!roomsLoading && !normalizedRooms.length && (
                      <MenuItem value="" disabled>
                        No rooms available
                      </MenuItem>
                    )}
                    {normalizedRooms.map((room) => (
                      <MenuItem key={room.id} value={room.id}>
                        {room.name} • {room.city}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    type="date"
                    label="Schedule Date"
                    value={scheduleDate}
                    onChange={(event) => setScheduleDate(event.target.value)}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                                         helperText={
                      roomsLoading
                        ? 'Loading rooms…'
                        : 'Choose the schedule date you wish to modify.'
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={loadSchedule}
                    fullWidth
                    disabled={!selectedRoomId || !scheduleDate || loadingSchedule}
                  >
                    {loadingSchedule ? 'Loading…' : 'Load'}
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        {loadedScheduleMeta && (
          <Card>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="subtitle1">Loaded Schedule</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {loadedScheduleMeta.roomName && (
                    <Chip
                      label={`Room: ${loadedScheduleMeta.roomName}`}
                      color="default"
                      size="small"
                    />
                  )}
                  {loadedScheduleMeta.date && (
                    <Chip label={`Date: ${loadedScheduleMeta.date}`} size="small" color="primary" />
                  )}
                  {loadedScheduleMeta.totalSessions !== undefined && (
                    <Chip
                      label={`${loadedScheduleMeta.totalSessions} sessions`}
                      size="small"
                      color="secondary"
                    />
                  )}
                  <Chip
                    label={`${blockedSlotCount} blocked ${blockedSlotCount === 1 ? 'slot' : 'slots'}`}
                    size="small"
                    color={blockedSlotCount ? 'warning' : 'default'}
                  />
                </Stack>
                {loadedScheduleMeta.lastUpdated && (
                  <Typography variant="caption" color="text.secondary">
                    Last updated: {new Date(loadedScheduleMeta.lastUpdated).toLocaleString()}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="h6">Sessions & Slots</Typography>
                  <Typography color="text.secondary">
                    Adjust session labels, window start times, notes, and per-slot settings. Toggle
                    “Block slot” to hide a slot from public booking without deleting it.
                  </Typography>
                </Box>
                <Button variant="outlined" onClick={handleAddSession}>
                  Add Session
                </Button>
              </Stack>

              <Stack spacing={2}>
                {sessions.map((session, sessionIndex) => (
                  <Card key={`session-${sessionIndex}`} variant="outlined">
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          spacing={1.5}
                        >
                          <Typography variant="subtitle1">
                            Session {session.sessionNumber || sessionIndex + 1}
                          </Typography>
                          <Button
                            color="error"
                            onClick={() => handleRemoveSession(sessionIndex)}
                            disabled={sessions.length === 1}
                          >
                            Remove Session
                          </Button>
                        </Stack>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Session Label"
                              value={session.sessionLabel}
                              onChange={handleSessionFieldChange(sessionIndex, 'sessionLabel')}
                              required
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              type="time"
                              label="Time Slot"
                              value={session.timeSlot}
                              onChange={handleSessionFieldChange(sessionIndex, 'timeSlot')}
                              required
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={5}>
                            <TextField
                              label="Session Note"
                              value={session.specialNote}
                              onChange={handleSessionFieldChange(sessionIndex, 'specialNote')}
                              fullWidth
                              placeholder="Optional note for this session."
                            />
                          </Grid>
                        </Grid>

                        <Divider />

                        <Stack spacing={2}>
                          <Typography variant="body2" fontWeight={600}>
                            Slots
                          </Typography>
                          {session.slots.map((slot, slotIndex) => (
                            <Card key={`slot-${sessionIndex}-${slotIndex}`} variant="outlined">
                              <CardContent>
                                <Stack spacing={2}>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                      <TextField
                                        label="Slot Label"
                                        value={slot.slotLabel}
                                        onChange={handleSlotFieldChange(
                                          sessionIndex,
                                          slotIndex,
                                          'slotLabel'
                                        )}
                                        required
                                        fullWidth
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                      <TextField
                                        type="number"
                                        label="Max Capacity"
                                        value={slot.maxCapacity}
                                        onChange={handleSlotFieldChange(
                                          sessionIndex,
                                          slotIndex,
                                          'maxCapacity'
                                        )}
                                        required
                                        fullWidth
                                        inputProps={{ min: 1 }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                      <TextField
                                        type="number"
                                        label="Default Price"
                                        value={slot.defaultPrice}
                                        onChange={handleSlotFieldChange(
                                          sessionIndex,
                                          slotIndex,
                                          'defaultPrice'
                                        )}
                                        fullWidth
                                        inputProps={{ min: 0, step: 1 }}
                                        placeholder="Optional"
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                      <TextField
                                        label="Special Note"
                                        value={slot.specialNote}
                                        onChange={handleSlotFieldChange(
                                          sessionIndex,
                                          slotIndex,
                                          'specialNote'
                                        )}
                                        fullWidth
                                        placeholder="Optional slot note."
                                      />
                                    </Grid>
                                  </Grid>
                                  <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                    spacing={1}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={slot.isBlocked}
                                          onChange={handleSlotBlockedToggle(sessionIndex, slotIndex)}
                                        />
                                      }
                                      label="Block slot from bookings"
                                    />
                                    {slot.isBlocked && (
                                      <Chip
                                        label="Blocked"
                                        color="warning"
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                    <Button
                                      color="error"
                                      size="small"
                                      onClick={() => handleRemoveSlot(sessionIndex, slotIndex)}
                                      disabled={session.slots.length === 1}
                                    >
                                      Remove Slot
                                    </Button>
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </Card>
                          ))}
                          <Button
                            variant="text"
                            onClick={() => handleAddSlot(sessionIndex)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Add Slot
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" spacing={2}>
          <Button variant="text" color="inherit" onClick={handleResetEditor}>
            Reset Editor
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {submitLoading ? 'Saving…' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default EditSchedulePage;